import { cloneDeep } from 'lodash';
import React from 'react';
import { Select } from '@meitu/saas-ui';
import SliderInput from '@meitu/saas-ui/es/slider-input';
import { Editor } from '@meitu/saas-editor-core/es/index';
import { ImageLayer } from '@meitu/saas-editor-core/es/layers/image-layer';
import { IImageLayerProps, ImageEffectStrokeType, LayerType } from '@meitu/saas-editor-core/es/version-types';
import IconFont from '@meitu/xdesign-icon-font';
import { ColorGroup } from '@meitu/xdesign-widgets';
import LabelAction from '@/components/label-action';
import { useLocaleStore } from '@/i18n/index';
import EffectItem, { EffectItemShowTitle } from '../components/effect-item/effect-item';
import { getDefaultStrokeValue, getStrokeTypeOptions } from '../constant';
import { useActiveData } from '../hook';
import { getColorAlpha, transferColorOpacity } from '../utils';

const ImageStrokeItem: React.FC<{
    funcStatTrack: Function;
    editor: Editor;
    theme: string;
}> = ({ funcStatTrack, editor, theme }) => {
    const { $t } = useLocaleStore();
    const { activeLayer, setActiveData } = useActiveData();
    if (!activeLayer || activeLayer.layerType !== LayerType.IMAGE_LAYER) return null;

    const imageData = activeLayer.data as IImageLayerProps;
    const effect = imageData.effect;
    const doFuncStatTrack = () => {
        funcStatTrack({
            useId: 19,
            downloadId: 19,
            clickId: 49,
        });
    };
    const addStroke = () => {
        if (!imageData.effect) {
            imageData.effect = {
                effectAlpha: 1,
                glow: [],
                shadow: [],
                stroke: [getDefaultStrokeValue()],
                screenshotUrl: '',
            };
        } else {
            imageData.effect.stroke = [getDefaultStrokeValue()];
        }
        const newLayer = cloneDeep(activeLayer);
        editor.getInstance(ImageLayer).changeImageEffectAttr(newLayer, undefined, 'effectStroke');
        setActiveData(newLayer);
        doFuncStatTrack();
    };

    if (!effect || !effect.stroke || effect.stroke.length === 0) {
        return (
            <EffectItem
                title={$t((v) => v.imageEffectPanel.imageStrokeItem.stroke)}
                iconName="BorderBold"
                handleClick={() => {
                    addStroke();
                }}
            />
        );
    }

    const stroke = effect.stroke[0];

    const updateData = (key: string, value: any, isEnd = false, stopUpdate = false) => {
        if (isEnd) {
            doFuncStatTrack();
        }

        if (stopUpdate || !imageData.effect) {
            return;
        }

        if (imageData.effect.stroke[0]) {
            imageData.effect.stroke[0][key] = value;
        }
        const newLayer = cloneDeep(activeLayer);
        editor.getInstance(ImageLayer).changeImageEffectAttr(newLayer, undefined, 'effectStroke');
        setActiveData(newLayer);
    };

    const visibleClick = () => {
        const originVisible = stroke.hidden;
        updateData('hidden', !originVisible, true);
    };

    const delClick = () => {
        if (!imageData.effect) {
            return;
        }
        imageData.effect.stroke = [];
        const newLayer = cloneDeep(activeLayer);
        editor.getInstance(ImageLayer).changeImageEffectAttr(newLayer, undefined, 'effectStroke');
        setActiveData(newLayer);
        doFuncStatTrack();
    };

    const colorChange = (color: string, isEnd = false) => {
        updateData('color', color, isEnd, isEnd);
    };

    const widthChange = (width: number, isEnd = false) => {
        updateData('width', width, isEnd, isEnd);
    };

    const alphaChange = (alpha: number, isEnd = false) => {
        updateData('color', transferColorOpacity(stroke.color, alpha), isEnd, isEnd);
    };

    const strokeTypeChange = (strokeType: ImageEffectStrokeType) => {
        updateData('strokeType', strokeType, true);
    };

    return (
        <div className="c-image-effect-item-content">
            <EffectItemShowTitle
                title={$t((v) => v.imageEffectPanel.imageStrokeItem.stroke)}
                iconName="BorderBold"
                handleVisibleClick={visibleClick}
                handleDelClick={delClick}
                visible={!stroke.hidden}
            />

            <div className="content">
                <LabelAction
                    title={$t((v) => v.imageEffectPanel.imageStrokeItem.color)}
                    align
                    style={{ marginBottom: '8px' }}
                >
                    <ColorGroup
                        popupTriggerSize={{
                            width: 58,
                            height: 26,
                        }}
                        popupOverlayClassName="c-markup-panel__color-picker"
                        placement="topLeft"
                        color={stroke.color}
                        setColor={(val) => {
                            colorChange(val);
                        }}
                        setColorAfter={(val, _, { isBrandColor }) => {
                            colorChange(val, true);
                            if (isBrandColor) {
                                window._stat_track?.('hb_layers_func_use', {
                                    func_id: '9_107',
                                });
                            }
                        }}
                        showIcon={false}
                        onToggleBizTab={(bizTab) => {
                            if (bizTab === 'team') {
                                window._stat_track?.('hb_layers_func_click', {
                                    func_id: '9_107',
                                });
                            }
                        }}
                    />
                </LabelAction>

                <LabelAction title={$t((v) => v.imageEffectPanel.imageStrokeItem.scope)} align>
                    <div
                        style={{
                            width: '100%',
                        }}
                    >
                        <SliderInput
                            min={0}
                            max={100}
                            value={stroke.width * 100}
                            disabled={stroke.strokeType === ImageEffectStrokeType.tornPaper}
                            precision={1}
                            showStepBtn
                            type="horizontal"
                            onChange={(val: number) => {
                                widthChange(val / 100);
                            }}
                            onAfterChange={(val: number) => {
                                widthChange(val / 100, true);
                            }}
                            onClick={() => {}}
                        />
                    </div>
                </LabelAction>

                <LabelAction title={$t((v) => v.imageEffectPanel.imageStrokeItem.opacity)} align>
                    <div
                        style={{
                            width: '100%',
                        }}
                    >
                        <SliderInput
                            min={0}
                            max={100}
                            disabled={stroke.strokeType === ImageEffectStrokeType.tornPaper}
                            value={getColorAlpha(stroke.color)}
                            precision={1}
                            showStepBtn
                            type="horizontal"
                            onChange={(val: number) => {
                                alphaChange(val);
                            }}
                            onAfterChange={(val: number) => {
                                alphaChange(val, true);
                            }}
                            onClick={() => {}}
                        />
                    </div>
                </LabelAction>

                <LabelAction title={$t((v) => v.imageEffectPanel.imageStrokeItem.style)} align>
                    <div
                        style={{
                            width: '100%',
                        }}
                    >
                        <Select
                            value={stroke.strokeType}
                            onChange={strokeTypeChange}
                            popupClassName="c-image-stroke-item-select"
                            suffixIcon={<IconFont name="ChevronDownBold" />}
                        >
                            {getStrokeTypeOptions(theme).map((item) => {
                                return (
                                    <Select.Option value={item.value} label={item.label} key={item.value}>
                                        <div style={{ width: '100%', height: '20px' }}>
                                            <img alt={item.label} width="90%" height="auto" src={item.url} />
                                        </div>
                                    </Select.Option>
                                );
                            })}
                        </Select>
                    </div>
                </LabelAction>
            </div>
        </div>
    );
};

export default ImageStrokeItem;
